import { MaterialType, Attachment, Pannel } from "types/canvasTypes";

interface ShopifyDraftOrderInput {
  lineItems: {
    variantId: string;
    quantity: number;
  }[];
  email?: string;
  shippingAddress?: {
    firstName?: string;
    lastName?: string;
    address1?: string;
    city?: string;
    province?: string;
    zip?: string;
    country?: string;
  };
  note?: string;
}

// Store types
export type StoreType = "bc" | "gr" | "rf";

const SHOPIFY_ACCESS_TOKEN = process.env.REACT_APP_SHOPIFY_ACCESS_TOKEN;
const SHOPIFY_GR_ACCESS_TOKEN = process.env.REACT_APP_SHOPIFY_GR_ACCESS_TOKEN;
const SHOPIFY_RF_ACCESS_TOKEN = process.env.REACT_APP_SHOPIFY_RF_ACCESS_TOKEN;

// Base URLs for Shopify stores (using proxy for development)
const SHOPIFY_BC_URL = "/api";
const SHOPIFY_GR_URL = "/gr";
const SHOPIFY_RF_URL = "/rf";

// Store configuration
const STORE_CONFIG = {
  bc: {
    endpoint: `${SHOPIFY_BC_URL}/admin/api/2024-01/graphql`,
    accessToken: SHOPIFY_ACCESS_TOKEN,
  },
  gr: {
    endpoint: `${SHOPIFY_GR_URL}/admin/api/2024-01/graphql`,
    accessToken: SHOPIFY_GR_ACCESS_TOKEN,
  },
  rf: {
    endpoint: `${SHOPIFY_RF_URL}/admin/api/2024-01/graphql`,
    accessToken: SHOPIFY_RF_ACCESS_TOKEN,
  },
};

// Door variant IDs by store and length
const DOOR_VARIANT_IDS: Record<StoreType, Record<number, string>> = {
  bc: {
    48: "gid://shopify/ProductVariant/55259242135921",
    42: "gid://shopify/ProductVariant/55259242135921",
    36: "gid://shopify/ProductVariant/55259242201457",
  },
  gr: {
    48: "gid://shopify/ProductVariant/44359283900601", // 46" door
    42: "gid://shopify/ProductVariant/44359285309625", // 39.6" door
    36: "gid://shopify/ProductVariant/44359285342393", // 33.6" door
  },
  rf: {
    48: "gid://shopify/ProductVariant/50443951308964", // 48" / 35.4" door
    42: "gid://shopify/ProductVariant/50443950882980", // 42" / 35.4" door
    36: "gid://shopify/ProductVariant/50443950850212", // 34" / 35.4" door
  },
};

// Rate limiting protection
let lastApiCall = 0;
const MIN_API_CALL_INTERVAL = 1000; // 1 second minimum between calls

export async function createDraftOrder(
  input: ShopifyDraftOrderInput,
  store: StoreType = "bc"
) {
  // Rate limiting protection
  const now = Date.now();
  const timeSinceLastCall = now - lastApiCall;

  if (timeSinceLastCall < MIN_API_CALL_INTERVAL) {
    const waitTime = MIN_API_CALL_INTERVAL - timeSinceLastCall;
    console.log(`⏳ Rate limiting: waiting ${waitTime}ms before next API call`);
    await new Promise((resolve) => setTimeout(resolve, waitTime));
  }

  lastApiCall = Date.now();

  console.log(
    "Creating draft order with input:",
    JSON.stringify(input, null, 2),
    "for store:",
    store
  );

  const storeConfig = STORE_CONFIG[store];
  if (!storeConfig) {
    throw new Error(`Invalid store: ${store}`);
  }

  // Debug: Log the endpoint and token presence
  console.log("🔍 Store config:", {
    store,
    endpoint: storeConfig.endpoint,
    hasToken: !!storeConfig.accessToken,
    tokenLength: storeConfig.accessToken?.length || 0,
  });

  const mutation = `
    mutation draftOrderCreate($input: DraftOrderInput!) {
      draftOrderCreate(input: $input) {
        draftOrder {
          id
          order {
            id
          }
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  const requestBody = {
    query: mutation,
    variables: {
      input: {
        lineItems: input.lineItems,
        email: input.email,
        shippingAddress: input.shippingAddress,
        note: input.note,
      },
    },
  };

  try {
    console.log("🚀 Making request to:", storeConfig.endpoint);
    console.log("🚀 Request body:", JSON.stringify(requestBody, null, 2));

    const response = await fetch(storeConfig.endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Shopify-Access-Token": storeConfig.accessToken || "",
      },
      body: JSON.stringify(requestBody),
    });

    console.log("📥 Response status:", response.status);
    console.log(
      "📥 Response headers:",
      Object.fromEntries(response.headers.entries())
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error("❌ HTTP Error:", response.status, errorText);

      // Handle rate limiting specifically
      if (response.status === 429) {
        throw new Error(
          `Rate limit exceeded. Please wait before making another request.`
        );
      }

      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const result = await response.json();
    console.log("Shopify API Response:", JSON.stringify(result, null, 2));

    if (result.errors) {
      console.error("GraphQL Errors:", result.errors);
      throw new Error(result.errors[0].message);
    }

    if (result.data.draftOrderCreate.userErrors.length > 0) {
      console.error("User Errors:", result.data.draftOrderCreate.userErrors);
      throw new Error(result.data.draftOrderCreate.userErrors[0].message);
    }

    return result.data.draftOrderCreate.draftOrder;
  } catch (error) {
    console.error("Error creating draft order:", error);
    throw error;
  }
}

type OrderDetails = {
  id: string;
  createdBy: string;
  email: string;
  name: string;
  includeAttachement: boolean;
  material: MaterialType;
};

export function convertQuotationToDraftOrder(
  orderDetails: OrderDetails,
  selectedLength: number,
  panels: Pannel[],
  attachments: Attachment[],
  numberOfDoors: number = 0,
  store: StoreType = "bc"
): ShopifyDraftOrderInput {
  const [firstName, ...lastNameParts] = orderDetails.name.split(" ");
  const lastName = lastNameParts.join(" ");

  // Create line items for panels, grouping by variant ID and summing quantities
  const panelVariantMap = new Map<string, number>();
  panels.forEach((panel) => {
    let variantId: string | undefined;

    switch (store) {
      case "bc":
        variantId = panel.bcVariantId;
        break;
      case "gr":
        variantId = panel.grVariantId;
        break;
      case "rf":
        variantId = panel.rfVariantId;
        break;
    }

    if (variantId) {
      const currentCount = panelVariantMap.get(variantId) || 0;
      panelVariantMap.set(variantId, currentCount + 1);
    }
  });

  const panelLineItems = Array.from(panelVariantMap.entries()).map(
    ([variantId, quantity]) => ({
      variantId,
      quantity,
    })
  );

  // Create line items for attachments, using appropriate variant ID based on material color and store
  const attachmentVariantMap = new Map<string, number>();
  attachments
    .filter((attachment) => {
      if (attachment.count <= 0) return false;

      if (orderDetails.material === MaterialType.Black) {
        switch (store) {
          case "bc":
            return !!attachment.bcVariantBlackId;
          case "gr":
            return !!attachment.grVariantBlackId;
          case "rf":
            return !!attachment.rfVariantBlackId;
        }
      } else if (orderDetails.material === MaterialType.Silver) {
        switch (store) {
          case "bc":
            return !!attachment.bcVariantSilverId;
          case "gr":
            return !!attachment.grVariantSilverId;
          case "rf":
            return !!attachment.rfVariantSilverId;
        }
      }
      return false;
    })
    .forEach((attachment) => {
      let variantId: string;

      if (orderDetails.material === MaterialType.Black) {
        switch (store) {
          case "bc":
            variantId = attachment.bcVariantBlackId!;
            break;
          case "gr":
            variantId = attachment.grVariantBlackId!;
            break;
          case "rf":
            variantId = attachment.rfVariantBlackId!;
            break;
          default:
            throw new Error(`Invalid store: ${store}`);
        }
      } else {
        switch (store) {
          case "bc":
            variantId = attachment.bcVariantSilverId!;
            break;
          case "gr":
            variantId = attachment.grVariantSilverId!;
            break;
          case "rf":
            variantId = attachment.rfVariantSilverId!;
            break;
          default:
            throw new Error(`Invalid store: ${store}`);
        }
      }

      const currentCount = attachmentVariantMap.get(variantId) || 0;
      attachmentVariantMap.set(variantId, currentCount + attachment.count);
    });

  const attachmentLineItems = Array.from(attachmentVariantMap.entries()).map(
    ([variantId, quantity]) => ({
      variantId,
      quantity,
    })
  );

  // Combine all line items
  const lineItems = [...panelLineItems, ...attachmentLineItems];

  console.log("selectedLength", selectedLength);
  console.log("numberOfDoors", numberOfDoors);
  console.log("store", store);

  // Add door products for all stores (BC, GR, and RF)
  if (numberOfDoors > 0) {
    const doorVariantId = DOOR_VARIANT_IDS[store]?.[selectedLength];
    if (doorVariantId) {
      lineItems.push({
        variantId: doorVariantId,
        quantity: numberOfDoors,
      });
    }
  }

  return {
    lineItems,
    email: "application@gmail.com",
    shippingAddress: {
      firstName: firstName || "",
      lastName: lastName || "",
    },
    note:
      "Created by Application id: https://quotation-gamma.vercel.app/quotations/" +
      orderDetails.id,
  };
}

export async function createTestOrder(store: StoreType = "bc") {
  const testOrder: ShopifyDraftOrderInput = {
    lineItems: [
      {
        variantId: "gid://shopify/ProductVariant/55237139169649",
        quantity: 1,
      },
    ],
    email: "fu@example.com",
    shippingAddress: {
      firstName: "full",
      lastName: "name",
      address1: "123 Test Street",
      city: "Test City",
      province: "QC",
      zip: "H1H 1H1",
      country: "Canada",
    },
    note: "Test order for glass panel",
  };

  return createDraftOrder(testOrder, store);
}
